import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth';
import type { SyncLogBatchDetailsResponse } from '@/types/api.types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // 允许跨域携带 httpOnly Cookie
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 是否正在刷新 token
let isRefreshing = false;
// 等待刷新的请求队列
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // 跳过登录/注册/刷新 token 的请求，避免死循环
    const url = originalRequest?.url || '';
    if (url.includes('/auth/login/') || url.includes('/auth/register/') || url.includes('/auth/refresh/')) {
      return Promise.reject(error);
    }

    // Subscription required / expired
    if (error.response?.status === 402) {
      const data = error.response.data as { message?: string; detail?: string };
      if (data.message === 'SUBSCRIPTION_REQUIRED') {
        // 不自动跳转，让调用方处理弹窗
        console.warn('Subscription required:', data.detail);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // 正在刷新 token，将请求加入队列等待
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // 尝试刷新 token；refresh token 通过 httpOnly Cookie 自动携带
        const response = await authApi.refresh();
        const { access } = response.data;

        // 更新 auth store 中的 token
        useAuthStore.setState({ token: access });

        // 处理队列中的请求
        processQueue(null, access);

        // 重试原始请求
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // 刷新失败，退出登录
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// 后端错误码映射（code → 中文提示）
export const API_ERROR_CODES: Record<string, string> = {
  EFLIGHT_INVALID_CREDENTIALS: 'eFlight 用户名或密码错误，请检查凭证',
  EFLIGHT_NETWORK_ERROR: 'eFlight 服务连接失败，请稍后重试',
  FSOP_INVALID_MESSID: 'FSOP 登录令牌无效或已过期，请重新获取 messid',
  FSOP_NETWORK_ERROR: 'FSOP 服务连接失败，请稍后重试',
  SUBSCRIPTION_REQUIRED: '需要订阅才能使用该功能',
  VALIDATION_ERROR: '请求参数校验失败',
};

/** 解析后端错误响应，优先使用 code 映射后中文提示 */
export const parseApiError = (error: any): { message: string; code?: string } => {
  const responseData = error?.response?.data;
  const code = responseData?.code;
  const defaultMessage = responseData?.message || error?.message || '操作失败，请稍后重试';
  return {
    message: (code && API_ERROR_CODES[code]) || defaultMessage,
    code,
  };
};

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    api.post('/auth/login/', { username, password }),

  register: (data: {
    username: string;
    password: string;
    passwordConfirm: string;
    email: string;
    displayName: string;
    phone?: string;
    eflightUsername?: string;
    eflightPassword?: string;
  }) => api.post('/auth/register/', data),

  /** 刷新 access token；refresh token 通过 httpOnly Cookie 自动携带 */
  refresh: () => api.post('/auth/refresh/', {}),

  /** 登出：后端清除 httpOnly refresh token Cookie */
  logout: () => api.post('/auth/logout/', {}),

  /** 验证邮箱 Token，成功后返回 JWT */
  verifyEmail: (token: string) =>
    api.get('/auth/verifyEmail/', { params: { token } }),
};

// User API
export const userApi = {
  getProfile: () => api.get('/user/profile/'),
  
  updateProfile: (data: {
    displayName?: string;
    email?: string;
    phone?: string;
    baseAirport?: string;
  }) => api.put('/user/profile/', data),
  
  updateEflightCredentials: (data: {
    eflightUsername: string;
    eflightPassword: string;
  }) => api.put('/user/eflight-credentials/', data),
  
  updateSyncSettings: (data: {
    syncEnabled?: boolean;
    syncMonths?: number;
    syncHistoryMonths?: number;
  }) => api.put('/user/sync-settings/', data),
  
  regenerateIcsToken: () =>
    api.post('/user/regenerate-ics-token/'),

  getReminderSettings: () =>
    api.get('/user/reminder_settings/') as Promise<{ data: { success: boolean; data: { landingReminderEnabled: boolean; remindAfterLandingMinutes: number } } }>,

  updateReminderSettings: (data: {
    landingReminderEnabled?: boolean;
    remindAfterLandingMinutes?: number;
  }) => api.put('/user/reminder_settings/', data),

  getBarkConfig: () =>
    api.get('/user/bark_key/') as Promise<{ data: { success: boolean; data: { barkKeyMask: string; barkServerUrl: string } } }>,

  updateBarkConfig: (data: {
    barkKey?: string;
    barkServerUrl?: string;
  }) => api.post('/user/bark_key/', data) as Promise<{
    data: {
      success: boolean;
      message: string;
      data?: { barkKeyMask: string; barkServerUrl: string };
    };
  }>,

  testBarkConfig: () =>
    api.post('/user/bark_key/') as Promise<{ data: { success: boolean; message: string } }>,

  getLinkSettings: () =>
    api.get('/user/link_settings/') as Promise<{ data: { success: boolean; data: LinkSettings } }>,

  updateLinkSettings: (data: Partial<LinkSettings>) =>
    api.put('/user/link_settings/', data) as Promise<{ data: { success: boolean; message: string } }>,
};

// Flight API
export const flightApi = {
  getFlights: (params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) => api.get('/flights/', { params }),
  
  getFlight: (id: number) => api.get(`/flights/${id}/`),
  
  getCalendar: (year: number, month: number) =>
    api.get('/flights/calendar/', { params: { year, month } }),
  
  getUpcoming: () => api.get('/flights/upcoming/'),

  getUpcomingLegs: () => api.get('/flights/upcoming-legs/'),

  getStats: () => api.get('/flights/stats/'),

  getMonthHours: () => api.get('/flights/month-hours/'),

  getRouteStats: (period: 'week' | 'month' | 'year', excludeBase?: boolean) =>
    api.get('/flights/route-stats/', { params: { period, excludeBase } }),

  autoLinkLegs: () =>
    api.post('/flights/legs/auto-link/') as Promise<{ data: { success: boolean; data: { taskId: string } } }>,

  getUnlinkedLegsCount: () =>
    api.get('/flights/legs/unlinked/') as Promise<{ data: { success: boolean; data: { count: number } } }>,

  getUnlinkedLegs: (params?: { page?: number; page_size?: number }) =>
    api.get('/flights/legs/unlinked/', { params }) as Promise<{ data: { success: boolean; data: { results: UnlinkedLeg[]; count: number; page: number; total_pages: number } } }>,

  getSkippedLegs: (params?: { page?: number; page_size?: number }) =>
    api.get('/flights/legs/unlinked/', { params: { ...params, show_skipped: 'true' } }) as Promise<{ data: { success: boolean; data: { results: UnlinkedLeg[]; count: number; page: number; total_pages: number } } }>,

  skipLeg: (id: number, reason?: string) =>
    api.post(`/flights/legs/${id}/skip/`, { reason: reason ?? '' }) as Promise<{ data: { success: boolean; message: string } }>,

  unskipLeg: (id: number) =>
    api.post(`/flights/legs/${id}/unskip/`) as Promise<{ data: { success: boolean; message: string } }>,

  getAutoLinkLogs: () =>
    api.get('/flights/legs/auto-link-logs/') as Promise<{ data: { success: boolean; data: AutoLinkLogEntry[] } }>,
};

/** 机场 — 后端直接返回 camelCase 格式 */
export interface Airport {
  code: string;
  iata: string;
  icao: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
  source: string;
  createdAt: string;
  updatedAt: string;
}

// Airport API
export const airportApi = {
  get: (code: string) =>
    api.get(`/airports/${encodeURIComponent(code)}/`),

  search: (q: string) =>
    api.get('/airports/search/', { params: { q } }),

  upsertManual: (code: string, data: {
    lat: number;
    lng: number;
    name?: string;
    city?: string;
    country?: string;
  }) => api.put(`/airports/${encodeURIComponent(code)}/manual/`, data),
};

// Unlinked leg (from FlightLegListSerializer)
export interface UnlinkedLeg {
  id: number;
  flightNo: string;
  flightDate: string;
  depCode: string;
  arrCode: string;
  depShortName: string;
  arrShortName: string;
  planDepTime: string | null;
  postName: string;
  linkSkipped: boolean;
  linkSkipReason: string;
}

export interface AutoLinkLogEntry {
  id: number;
  status: 'running' | 'success' | 'partial' | 'failed';
  linkedCount: number;
  ambiguousCount: number;
  manualSkippedCount: number;
  errorMessage: string;
  triggeredAt: string;
  completedAt: string | null;
}

// Link Settings
export interface LinkSettings {  autoLinkAfterEflightSync: boolean;
  autoLinkAfterFsopSync: boolean;
  unlinkedReminderEnabled: boolean;
  unlinkedReminderDays: number;
  lastAutoLinkAt: string | null;
  lastAutoLinkLinked: number;
  lastAutoLinkSkipped: number;
}

// Sync API
export const syncApi = {
  getLogs: (params?: { page?: number; pageSize?: number }) =>
    api.get('/sync/logs/', { params }) as Promise<{ data: PaginatedResult<import('@/types').SyncLog> }>,

  getLog: (id: number) => api.get(`/sync/logs/${id}/`),

  getLogBatchDetails: (id: number) =>
    api.get(`/sync/logs/${id}/batch_details/`) as Promise<{ data: SyncLogBatchDetailsResponse }>,

  trigger: (months?: number, historyMonths?: number) =>
    api.post('/sync/trigger/', { months, historyMonths }),

  getStatus: () => api.get('/sync/status/'),
};

// Admin API
export const adminApi = {
  getUsers: () => api.get('/admin/users/'),

  getUser: (id: number) => api.get(`/admin/users/${id}/`),

  updateUser: (id: number, data: object) =>
    api.put(`/admin/users/${id}/`, data),

  deleteUser: (id: number) => api.delete(`/admin/users/${id}/`),

  syncAll: () => api.post('/sync/all/'),

  getStats: () => api.get('/admin/stats/'),
};

// =============================================================================
// FSOP API
// =============================================================================

/** DRF 标准分页结果 */
export interface PaginatedResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** 凭证 — 后端直接返回 camelCase 格式 */
export interface FsopCredential {
  id: number;
  archiveNo: string;
  licenseNum: string;
  phone: string;
  // messid 完整值不再返回，仅返回掩码用于识别
  messidMask?: string;
  isActive: boolean;
  checkedAt: string | null;
  isValid: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/** 视图配置 */
export interface ViewConfig {
  id: number;
  name: string;
  selectedFields: string[];
  sortBy: string;
  sortOrder: string;
  isDefault: boolean;
  pageSize: number;
  filters: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface ViewConfigPayload {
  name: string;
  selectedFields: string[];
  sortBy: string;
  sortOrder: string;
  isDefault: boolean;
  pageSize: number;
  filters: Record<string, string>;
}

/** 飞行记录 — 后端直接返回 camelCase 格式 */
export interface FlightLog {
  id: number;
  recordId: string;
  airDate: string;
  airNum: string;
  airModel: string;
  airline: string;
  departure: string;
  arrival: string;
  flightTime: number | null;
  nightTime: number | null;
  dayTime: number | null;
  recordType: string;
  state: string;
  [key: string]: any;
}

/** 筛选项枚举 — 后端直接返回 camelCase 格式 */
export interface FilterOptions {
  recordTypes: string[];
  airNums: string[];
  airModels: string[];
  airlines: string[];
}

/** 统计数据 — 后端直接返回 camelCase 格式 */
export interface Statistics {
  totalRecords: number;
  recordTypes: Record<string, number>;
  totalFlightHours: number;
  totalNightHours: number;
  recentRecords: number;
}

/** 同步任务 — 后端直接返回 camelCase 格式 */
export interface SyncTask {
  id: string;
  status: string;
  stage: string;
  message: string;
  errorMessage: string | null;
  isCancelled: boolean;
  currentPage: number;
  totalListRecords: number;
  newListRecords: number;
  skippedListRecords: number;
  totalDetailRecords: number;
  processedRecords: number;
  successCount: number;
  failedCount: number;
  progress: {
    overallPercentage: number;
    listPercentage: number;
    detailPercentage: number;
    list: { currentPage: number; totalRecords: number; newRecords: number; skippedRecords: number };
    detail: { totalRecords: number; processed: number; success: number; failed: number };
  };
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

/** 同步状态总览 — 后端直接返回 camelCase 格式 */
export interface SyncStatus {
  totalRecords: number;
  hasDetailCount: number;
  pendingDetailCount: number;
  isSyncing: boolean;
  hasCredential: boolean;
  lastSyncTask: SyncTask | null;
}

/** 下载日志 — 后端直接返回 camelCase 格式 */
export interface DownloadLog {
  id: number;
  level: string;
  stage: string;
  message: string;
  logId: string | null;
  createdAt: string;
  exceptionType: string | null;
  exceptionMessage: string | null;
}

export const fsopApi = {
  // ── 凭证 ───────────────────────────────────────────────────────────────
  getMyCredential: () =>
    api.get('/fsop/credentials/my/'),

  createCredential: (payload: { archiveNo: string; licenseNum: string; phone: string; messid?: string }) =>
    api.post('/fsop/credentials/', payload),

  updateCredential: (id: number, payload: { archiveNo: string; licenseNum: string; phone: string; messid?: string }) =>
    api.put(`/fsop/credentials/${id}/`, payload),

  deleteCredential: (id: number) =>
    api.delete(`/fsop/credentials/${id}/`),

  checkCredential: () =>
    api.post('/fsop/credentials/check/'),

  // ── 视图配置 ────────────────────────────────────────────────────────────
  getViewConfigs: (params?: { page?: number; pageSize?: number }) =>
    api.get('/fsop/view-configs/', { 
      params: {
        page: params?.page,
        pageSize: params?.pageSize,  // camelCase
      }
    }) as any,

  createViewConfig: (payload: ViewConfigPayload) =>
    api.post('/fsop/view-configs/', payload),

  updateViewConfig: (id: number, payload: ViewConfigPayload) =>
    api.put(`/fsop/view-configs/${id}/`, payload),

  deleteViewConfig: (id: number) =>
    api.delete(`/fsop/view-configs/${id}/`),

  setDefaultViewConfig: (id: number) =>
    api.post(`/fsop/view-configs/${id}/set_default/`),

  // ── 飞行记录 ────────────────────────────────────────────────────────────
  getFlightLogs: (params?: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    order?: string;
    search?: string;
    recordType?: string;
    airNum?: string;
    airModel?: string;
    airline?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    if (!params) return api.get('/fsop/flight-logs/') as any;
    const { page, pageSize, sortBy, order, search, recordType, airNum, airModel, airline, startDate, endDate } = params;
    return api.get('/fsop/flight-logs/', {
      params: {
        page,
        pageSize,      // camelCase
        sortBy,        // camelCase
        order,
        search,
        recordType,    // camelCase
        airNum,        // camelCase
        airModel,      // camelCase
        airline,
        startDate,     // camelCase
        endDate,       // camelCase
      },
    }) as any;
  },

  getFlightLog: (id: number) =>
    api.get(`/fsop/flight-logs/${id}/`) as Promise<{ data: { success: boolean; data: FlightLog } }>,

  getFlightLogsWithConfig: (configId: number, params: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    order?: string;
    search?: string;
    recordType?: string;
    airNum?: string;
    airModel?: string;
    airline?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const { page, pageSize, sortBy, order, search, recordType, airNum, airModel, airline, startDate, endDate } = params;
    return api.get('/fsop/flight-logs/with_config/', {
      params: {
        configId,      // camelCase
        page,
        pageSize,      // camelCase
        sortBy,        // camelCase
        order,
        search,
        recordType,    // camelCase
        airNum,        // camelCase
        airModel,      // camelCase
        airline,
        startDate,     // camelCase
        endDate,       // camelCase
      },
    }) as any;
  },

  getFilterOptions: () =>
    api.get('/fsop/flight-logs/filter_options/'),

  getStatistics: () =>
    api.get('/fsop/flight-logs/statistics/'),

  getRecentLogs: (limit = 10) =>
    api.get('/fsop/flight-logs/recent/', { params: { limit } }),

  // ── 航线地图 ────────────────────────────────────────────────────────────
  getRoutes: (params?: { year?: number; mergeDirection?: boolean }) =>
    api.get('/fsop/flight-logs/routes/', { params }),

  // ── 同步 ────────────────────────────────────────────────────────────────
  getSyncStatus: () =>
    api.get('/fsop/sync/status/'),

  startSync: () =>
    api.post('/fsop/sync/start/', {}),

  cancelSync: (taskId: string) =>
    api.post('/fsop/sync/cancel/', { taskId }),

  getSyncTasks: (params?: { page?: number; pageSize?: number }) =>
    api.get('/fsop/sync/tasks/', { params }) as any,

  getSyncProgress: (taskId: string) => {
    if (!taskId || taskId === 'undefined') {
      return Promise.reject(new Error('Invalid taskId'));
    }
    return api.get(`/fsop/sync/${taskId}/progress/`);
  },

  getSyncLogs: () =>
    api.get('/fsop/sync/logs/'),

  // ── 起落有效期 ────────────────────────────────────────────────────────────
  getLandingCurrency: () =>
    api.get('/fsop/flight-logs/landing_currency/'),
};

export default api;

// =============================================================================
// Notes API
// =============================================================================

export const notesApi = {
  syncToVault: (noteId: number) =>
    api.post(`/notes/${noteId}/sync_to_vault/`) as Promise<{ data: { success: boolean; message: string; data: { noteId: number; title: string; vaultSyncStatus: string } } }>,
};

// =============================================================================
// Vault Sync API
// =============================================================================

// =============================================================================
// Vault Sync API
// =============================================================================

export const vaultApi = {
  getConfig: () =>
    api.get('/notes/vault-config/') as Promise<{ data: { success: boolean; data: import('@/types/notes.types').VaultConfig } }>,

  updateConfig: (data: {
    webdavUrl?: string;
    webdavUser?: string;
    webdavPass?: string;
    basePath?: string;
    enabled?: boolean;
  }) =>
    api.post('/notes/vault-config/', data) as Promise<{ data: { success: boolean; message: string; data: import('@/types/notes.types').VaultConfig } }>,

  validateConfig: (data: {
    webdavUrl: string;
    webdavUser: string;
    webdavPass: string;
  }) =>
    api.post('/notes/vault-config/validate/', data) as Promise<{ data: import('@/types/notes.types').VaultValidateResult }>,

  sync: () =>
    api.post('/notes/vault-config/sync/') as Promise<{ data: { success: boolean; message: string; data: { taskId: string } } }>,

  getSyncStatus: () =>
    api.get('/notes/vault_sync_status/') as Promise<{ data: { success: boolean; data: import('@/types/notes.types').VaultSyncStatus } }>,

  getSyncLogs: (params?: { page?: number; pageSize?: number }) =>
    api.get('/notes/vault-config/sync-logs/', { params }) as Promise<{ data: { count: number; next: string | null; previous: string | null; results: import('@/types/notes.types').VaultSyncLog[] } }>,
};
